import {
  Alert,
  Badge,
  Button,
  Code,
  Divider,
  Group,
  Radio,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Role } from "./api";
import { OverrideHistory } from "./OverrideHistory";
import { useResetModelOverride } from "./useResetModelOverride";
import { useSaveModelOverride } from "./useSaveModelOverride";
import { useSupportedModels } from "./useSupportedModels";
import { agentRoleErrorText } from "./refusalText";

// Секция выбора модели роли на детальной странице роли. Зеркало
// RoleEditor для модели, но без собственного запроса detail — текущее
// состояние роли приходит пропсом из RoleEditorContent (единый запрос
// GET /agent_roles/{role} на всю карточку: промпт + модель). Два режима:
//   - «default» — эффективная модель = default_model плагина (пусто =
//     модель по умолчанию у провайдера). Кнопка «Откатить к default»
//     (DELETE /agent_roles/{role}/model) активна только если override
//     существует.
//   - «custom» — Select с моделями активного провайдера + «Сохранить»
//     (PUT /agent_roles/{role}/model). Backend валидирует модель по
//     списку провайдера (422 на чужой), но в Select только валидные —
//     до 422 не доводим.
// Под редактором — общая история override'ов роли, отфильтрованная по
// kind="model" (последние 10).

type Mode = "default" | "custom";

function modelLabel(model: string, t: TFunction): string {
  return model === "" ? t("agentRoles.model.providerDefault") : model;
}

function badgeForSource(role: Role): ReactElement | null {
  if (role.model_override_source === null) {
    return null;
  }
  return (
    <Badge color="grape">
      {/* источник override'а — те же подписи, что у промпта */}
      <SourceText source={role.model_override_source} />
    </Badge>
  );
}

function SourceText({
  source,
}: {
  readonly source: NonNullable<Role["model_override_source"]>;
}): ReactElement {
  const { t } = useTranslation();
  return <>{t(`agentRoles.source.${source}`)}</>;
}

function RoleModelEditorBody({ role }: { readonly role: Role }): ReactElement {
  const { t } = useTranslation();
  const supported = useSupportedModels();
  const saveMutation = useSaveModelOverride();
  const resetMutation = useResetModelOverride();

  const options = supported.data?.models ?? [];
  // Стартовый режим — custom, если override есть, иначе default. Тело
  // ремаунтится при смене роли И после save/reset (key в RoleModelEditor
  // зависит от effective_model + has_model_override), поэтому draft
  // стартует от свежего состояния без отдельного useEffect-синка.
  const [mode, setMode] = useState<Mode>(
    role.has_model_override ? "custom" : "default",
  );
  // draft: null = модель не выбрана (Select показывает placeholder,
  // «Сохранить» дизейблена). При override стартуем с текущей модели.
  const [draft, setDraft] = useState<string | null>(
    role.has_model_override ? role.effective_model : null,
  );

  const busy = saveMutation.isPending || resetMutation.isPending;

  const handleSave = (): void => {
    // draft гарантированно не null: «Сохранить» дизейблена при draft===null.
    // cast (а не runtime-guard) — чтобы не плодить недостижимую ветку под
    // 100%-coverage gate.
    saveMutation.mutate(
      { roleName: role.role_name, model: draft! },
      {
        onSuccess: () => {
          notifications.show({
            message: t("agentRoles.model.saveSuccess"),
            color: "green",
          });
        },
        onError: (error) => {
          notifications.show({
            message: t("agentRoles.model.saveError", {
              message: agentRoleErrorText(error, t),
            }),
            color: "red",
          });
        },
      },
    );
  };

  const handleReset = (): void => {
    resetMutation.mutate(role.role_name, {
      onSuccess: () => {
        notifications.show({
          message: t("agentRoles.model.resetSuccess"),
          color: "green",
        });
      },
      onError: (error) => {
        notifications.show({
          message: t("agentRoles.model.resetError", {
            message: agentRoleErrorText(error, t),
          }),
          color: "red",
        });
      },
    });
  };

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Title order={4}>{t("agentRoles.model.title")}</Title>
        {badgeForSource(role)}
      </Group>

      <Group gap="xs">
        <Text size="sm" c="dimmed">
          {t("agentRoles.model.effectiveLabel")}
        </Text>
        <Code>{modelLabel(role.effective_model, t)}</Code>
      </Group>

      <Radio.Group
        label={t("agentRoles.model.modeLabel")}
        value={mode}
        onChange={(value) => {
          setMode(value === "custom" ? "custom" : "default");
        }}
      >
        <Group gap="lg">
          <Radio value="default" label={t("agentRoles.model.modeDefault")} />
          <Radio value="custom" label={t("agentRoles.model.modeCustom")} />
        </Group>
      </Radio.Group>

      {mode === "default" ? (
        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {t("agentRoles.model.defaultLabel")}
            </Text>
            <Code>{modelLabel(role.default_model, t)}</Code>
          </Group>
          <Button
            variant="default"
            onClick={handleReset}
            loading={resetMutation.isPending}
            disabled={!role.has_model_override || busy}
          >
            {t("agentRoles.model.resetButton")}
          </Button>
        </Stack>
      ) : (
        <Stack gap="xs">
          {supported.isError ? (
            <Alert color="red" title={t("agentRoles.model.loadError")}>
              {agentRoleErrorText(supported.error, t)}
            </Alert>
          ) : options.length === 0 && !supported.isLoading ? (
            <Text c="dimmed">{t("agentRoles.model.noModels")}</Text>
          ) : (
            <Select
              label={t("agentRoles.model.selectLabel")}
              description={t("agentRoles.model.selectHint")}
              placeholder={t("agentRoles.model.selectPlaceholder")}
              data={options}
              value={draft}
              disabled={supported.isLoading}
              onChange={setDraft}
            />
          )}
          <Group justify="flex-start">
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={draft === null || busy}
            >
              {t("agentRoles.model.saveButton")}
            </Button>
          </Group>
        </Stack>
      )}

      <Divider />

      <Stack gap="xs">
        <Title order={5}>{t("agentRoles.model.history.title")}</Title>
        <OverrideHistory roleName={role.role_name} kind="model" />
      </Stack>
    </Stack>
  );
}

export function RoleModelEditor({
  role,
}: {
  readonly role: Role;
}): ReactElement {
  // key включает role_name + override-состояние: при смене роли И после
  // save/reset (меняется effective_model / has_model_override) тело
  // ремаунтится, draft/режим стартуют от свежих данных.
  return (
    <RoleModelEditorBody
      key={`${role.role_name}:${String(role.has_model_override)}:${role.effective_model}`}
      role={role}
    />
  );
}
